/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { adminSettingsApi } from './AdminSettingsApi';
import { MotorsportsError, MotorsportsErrorType } from 'services/motorsportsError/MotorsportsError';
import { AdminSettingsId, AdminUpdateResponse } from 'models/viewModels';
import { AxiosError } from 'axios';
import {
  AudioTranscriptionAdminSettingsObject,
  DefaultWorkspaceSettingsObject,
  RaceProgressionOriginAdminSettingsObject,
} from 'jotai/adminPage/adminSettings/AdminSettingsObjects';

class AdminSettingsService {
  async getAdminSettingsBySettingsId(settingsId: AdminSettingsId): Promise<string> {
    return adminSettingsApi
      .getAdminSettingsBySettingsId(settingsId)
      .then((response) => {
        return response.adminSettings.length > 0 && response.adminSettings[0].settings.length > 0
          ? response.adminSettings[0].settings
          : '{}';
      })
      .catch((error: AxiosError) => {
        throw new MotorsportsError(error.message, MotorsportsErrorType.Unknown, { cause: error });
      });
  }

  async getAudioTranscriptionAdminSettings(): Promise<AudioTranscriptionAdminSettingsObject> {
    return this.getAdminSettingsBySettingsId(AdminSettingsId.AUDIO_TRANSCRIPTION)
      .then((response) => {
        return JSON.parse(response) as AudioTranscriptionAdminSettingsObject;
      })
      .catch((error: AxiosError) => {
        throw new MotorsportsError(error.message, MotorsportsErrorType.Unknown, { cause: error });
      });
  }

  async getDefaultWorkspaceAdminSettings(): Promise<DefaultWorkspaceSettingsObject> {
    return this.getAdminSettingsBySettingsId(AdminSettingsId.DEFAULT_WORKSPACE)
      .then((response) => {
        return JSON.parse(response) as DefaultWorkspaceSettingsObject;
      })
      .catch((error: AxiosError) => {
        throw new MotorsportsError(error.message, MotorsportsErrorType.Unknown, { cause: error });
      });
  }

  async getRaceProgressionOriginsAdminSettings(): Promise<RaceProgressionOriginAdminSettingsObject> {
    return this.getAdminSettingsBySettingsId(AdminSettingsId.RACE_PROGRESSION_ORIGINS)
      .then((response) => {
        return JSON.parse(response) as RaceProgressionOriginAdminSettingsObject;
      })
      .catch((error: AxiosError) => {
        throw new MotorsportsError(error.message, MotorsportsErrorType.Unknown, { cause: error });
      });
  }

  async updateAdminSettings(settingsId: AdminSettingsId, settings: string): Promise<AdminUpdateResponse> {
    return adminSettingsApi
      .updateAdminSettings(settingsId, settings)
      .then((response) => {
        return response;
      })
      .catch((error: AxiosError) => {
        throw new MotorsportsError(error.message, MotorsportsErrorType.Unknown, { cause: error });
      });
  }
}

export const adminSettingsService = new AdminSettingsService();
